import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWhitelabel } from '../../contexts/WhitelabelContext';
import { toast } from 'react-hot-toast';
import { initializeCsrf, fetchWithCsrf } from '../../utils/csrf';

export default function ForgotPassword() {
  const { branding } = useWhitelabel();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      await initializeCsrf();

      const response = await fetchWithCsrf('/api/auth/password/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsEmailSent(true);
        toast.success(data.message || 'Password reset link sent!');
      } else {
        if (data.errors) {
          Object.keys(data.errors).forEach(key => {
            data.errors[key].forEach((message: string) => {
              toast.error(message);
            });
          });
        } else {
          toast.error(data.message || 'Failed to send reset link');
        }
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex flex-col items-center justify-center mb-6">
            {branding.hasWhitelabel && branding.companyLogo ? (
              <>
                <img
                  className="max-h-20 max-w-[300px] object-contain mb-4"
                  src={branding.companyLogo}
                  alt={branding.companyName}
                  style={{ maxHeight: '80px', maxWidth: '300px' }}
                />
                <h2 className="text-2xl font-bold text-white text-center">
                  {branding.companyName}
                </h2>
              </>
            ) : (
              <>
                <img
                  className="h-16 w-auto mb-3"
                  src="/assets/img/optimancer_logo.svg"
                  alt={branding.companyName}
                />
                <h2 className="text-2xl font-bold text-white text-center">
                  {branding.companyName}
                </h2>
              </>
            )}
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-slate-400">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {isEmailSent ? (
          <div className="mt-8 space-y-6">
            <div className="rounded-md bg-green-900/30 border border-green-700 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-400">
                    Check your email
                  </h3>
                  <div className="mt-2 text-sm text-green-300">
                    <p>
                      We've sent a password reset link to <strong>{email}</strong>.
                      Please check your inbox and follow the instructions.
                    </p>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs text-slate-400">
                      Didn't receive the email? Check your spam folder or{' '}
                      <button
                        onClick={() => setIsEmailSent(false)}
                        className="text-blue-400 hover:text-blue-300 underline"
                      >
                        try again
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/sign-in"
                className="font-medium text-blue-400 hover:text-blue-300"
              >
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-slate-700 placeholder-slate-500 text-white bg-slate-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Sending...' : 'Send reset link'}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/sign-in"
                className="font-medium text-blue-400 hover:text-blue-300"
              >
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
